import type { JobsiteBusinessContext, JobsiteBusinessHours } from "../../types/runtime-config";
import { businessFacts, serviceAreaLabel } from "../../lib/business-facts";
import { contactSecondary, resolveCtaOfferLine } from "../../lib/cta-defaults";
import { formatPhone, phoneHref, primaryMarketLabel } from "@servicepro/utils";
import type { BusinessFact } from "../../lib/business-facts";
import { weekHoursText } from "../../lib/opening-hours";

/**
 * What the hero says from the business record where the page itself says nothing:
 * the copy for empty fields, the second button, and the bold layout's facts.
 * Pure, so hero.config's render stays a list of props.
 */

/**
 * What the split hero's facts card says. Every line is something the owner
 * told us; nothing is a default.
 */
interface HeroFactsCardContent {
  facts: BusinessFact[];
  /** The owner's offer line ("Free estimates"), the one offer we store. */
  offer?: string | null;
  /** Open days, for the "open today" line. */
  hours?: JobsiteBusinessHours[];
  /** The zone those hours are in. */
  timezone?: string | null;
}

/** A hero button: its words and where it goes. */
interface HeroLink {
  text: string;
  url: string;
}

const
  /** The bold variant's words for the button that scrolls to the contact form: what a customer wants from it. */
  BOLD_CONTACT_CTA_TEXT = "Get a quote",

  /**
   * The primary button's words. The bold variant names a call button by the
   * number it dials, the way it names its second button; every other variant
   * keeps the owner's label.
   * @param {object} options What the button does and says.
   * @param {boolean} options.isBold Whether the bold variant is showing.
   * @param {boolean} options.calls Whether the button calls the business.
   * @param {string} options.text The resolved label, the owner's or the default.
   * @param {string | null} [options.phone] The number it calls.
   * @returns {string} The button's words.
   */
  heroPrimaryText = (options: { isBold: boolean; calls: boolean; text: string; phone?: string | null }): string => {
    const phone = options.phone ?? "";
    if (options.isBold && options.calls && phone !== "") {
      return `Call ${formatPhone(phone)}`;
    }
    return options.text;
  },

  /**
   * The headline and subheadline for empty fields. They say only what the record
   * does, the town and the name, the same shape as the generator's fallback, and
   * never speed or credentials.
   * @param {JobsiteBusinessContext} [business] The business in Puck's metadata.
   * @returns {{ headline: string; subheadline: string }} "Serving <town>" and "<Business> serves <town> and the surrounding area.", or as much as the record allows.
   */
  heroFallbackCopy = (business?: JobsiteBusinessContext): { headline: string; subheadline: string } => {
    const marketLabel = primaryMarketLabel(business?.primaryMarket),
      businessName = business?.name ?? "";
    if (marketLabel === "") {
      return { headline: businessName === "" ? "Our Services" : businessName, subheadline: "" };
    }
    return {
      headline: `Serving ${marketLabel}`,
      subheadline:
        businessName === ""
          ? `Serving ${marketLabel} and the surrounding area.`
          : `${businessName} serves ${marketLabel} and the surrounding area.`,
    };
  },

  /**
   * The second button: whichever of call and form the primary is not. It says
   * nothing the business hasn't (no "free", no "estimate", no promise about
   * response time); the owner's own label always occupies the primary button.
   *
   * It used to be the phone number unconditionally, so a business whose CTA is
   * phone-first ("Call Now", also the system default) shipped "Call Now" beside
   * "Call Us: (508) 272-1139", both the same tel: link.
   * @param {object} options What the primary does and what the business has.
   * @param {boolean} options.primaryCalls Whether the primary button already calls.
   * @param {string | null} [options.phone] The business phone.
   * @param {boolean} options.isBold Whether the bold variant is showing: it names the call button by its number alone, and the form button "Get a quote".
   * @param {string} [options.pagePath] The page the hero is on.
   * @param {readonly string[]} [options.pagePaths] Every page on the site.
   * @param {boolean} [options.onePage] Whether the site is one page, from its build.
   * @returns {HeroLink | null} The button, or null when one button is the honest answer.
   */
  heroSecondaryCta = (options: {
    primaryCalls: boolean;
    phone?: string | null;
    isBold: boolean;
    onePage?: boolean;
    pagePath?: string;
    pagePaths?: readonly string[];
  }): HeroLink | null => {
    const phone = options.phone ?? "";
    if (options.primaryCalls) {
      /* The way through for people who won't phone. Deliberately neutral: the owner
         chose a phone-first card, so borrowing "Get a Free Quote" from another card
         would put an offer on their site they never agreed to. */
      const contact = contactSecondary(options.pagePath, options.pagePaths, options.onePage) ?? null;
      if (contact !== null && options.isBold) {
        return { ...contact, text: BOLD_CONTACT_CTA_TEXT };
      }
      return contact;
    }
    if (phone !== "") {
      return {
        text: options.isBold ? `Call ${formatPhone(phone)}` : `Call Us: ${formatPhone(phone)}`,
        url: phoneHref(phone),
      };
    }
    // No phone, and the primary is the form: never invent a number.
    return null;
  },

  /**
   * The bold layout's facts card and the towns above its headline, read live from
   * the business at render, so a fact the owner changes shows on the next publish
   * without regenerating the page.
   * @param {JobsiteBusinessContext} [business] The business in Puck's metadata.
   * @returns {{ factsCard: HeroFactsCardContent; serviceAreaLabel: string }} The card's content and the label.
   */
  heroBusinessFacts = (business?: JobsiteBusinessContext): { factsCard: HeroFactsCardContent; serviceAreaLabel: string } => ({
    factsCard: {
      facts: businessFacts(business),
      hours: business?.hours,
      offer: resolveCtaOfferLine(business),
      timezone: business?.timezone,
    },
    serviceAreaLabel: serviceAreaLabel(business),
  }),

  /**
   * Hides the editor fields the bold variant ignores. It is always a dark band
   * with light type, so overlay and text colour do nothing; its facts come from
   * the business, never chips; and the towns replace the keyword badge whenever
   * the business has any. Its headline is the trade and the town ("Plumber in
   * Boulder, CO"), short on purpose and naming the trade rather than a service,
   * so the editor stops warning that it is too short or names no service.
   * @param {Record<string, unknown>} visible The fields the panel will show; changed in place.
   * @param {JobsiteBusinessContext} [business] The business in Puck's metadata.
   */
  hideFieldsBoldIgnores = (visible: Record<string, unknown>, business?: JobsiteBusinessContext): void => {
    delete visible.overlay;
    delete visible.textColor;
    delete visible.trustIndicators;
    if (serviceAreaLabel(business) !== "") {
      delete visible.keywordBadge;
    }
    const headline = visible.headline as { metadata?: { guardrails?: Record<string, unknown> } } | undefined;
    if (headline?.metadata?.guardrails !== undefined) {
      visible.headline = {
        ...headline,
        metadata: { ...headline.metadata, guardrails: { ...headline.metadata.guardrails, minLength: undefined, requireService: false } },
      };
    }
  },

  /**
   * Whether the facts card will have anything to say, decided from data the
   * server has, so the layout never shifts when today's hours fill in.
   * @param {HeroFactsCardContent | undefined} content The card's content.
   * @returns {boolean} True when the card renders.
   */
  hasFactsCardContent = (content: HeroFactsCardContent | undefined): content is HeroFactsCardContent =>
    content !== undefined &&
    (content.facts.length > 0 || (content.offer?.trim() ?? "") !== "" || weekHoursText(content.hours) !== "");

export type { HeroFactsCardContent };
export { hasFactsCardContent, heroBusinessFacts, heroFallbackCopy, heroPrimaryText, heroSecondaryCta, hideFieldsBoldIgnores };
